import { API_URL } from "@/config/constants";
import { buildUrl } from "@/utils/buildUrl";
import axios from "axios";
import { IFilter } from "../api.types";
import {
    IDocumentProcessingStatus,
    IDuplicateCheckResponse,
    ILibraryComparisonUpdate,
    ILibraryDocument,
    ILibraryDocumentMoveRequest,
    ILibraryDocumentRelated,
    ILibraryDocumentsPage,
    ILibraryDocumentUpdate,
    ILibraryListComparison,
    ILibraryListPage,
    ILibraryListParams,
    ILibraryListScope,
    ILibraryScopeUpdate,
    ILibraryUploadConfirm,
    IPresignedUploadResponse,
} from "./library-documents.types";

const API = `${API_URL}/library`;

// Separate axios instance without Authorization header for direct S3 uploads
const storageAxios = axios.create({
    headers: {},
});
delete storageAxios.defaults.headers.common["Authorization"];

export const getLibraryDocuments = async (
    page: number = 1,
    size: number = 50,
    filter?: IFilter,
    folderId?: number | null,
    organizationId?: number | null,
    search?: string | null,
    isKnowledgeBase?: boolean | null
) => {
    const url = buildUrl(`${API}/documents`, {
        page,
        size,
        ...filter,
        ...(folderId ? { folder_id: folderId } : {}),
        ...(organizationId ? { organization_id: organizationId } : {}),
        ...(search ? { search } : {}),
        // `!= null` (not truthiness): `false` is a meaningful value to forward.
        ...(isKnowledgeBase != null
            ? { is_knowledge_base: isKnowledgeBase }
            : {}),
    });
    const result = await axios.get<ILibraryDocumentsPage>(url);
    return result.data;
};

export const searchLibraryDocuments = async (
    q: string,
    page: number = 1,
    size: number = 50,
    folderId?: number | null
) => {
    const url = buildUrl(`${API}/search`, {
        q,
        page,
        size,
        ...(folderId ? { folder_id: folderId } : {}),
    });
    const result = await axios.get<ILibraryDocumentsPage>(url);
    return result.data;
};

export const getLibraryDocument = async (
    id: number,
    includeDownloadUrl = false
) => {
    const result = await axios.get<ILibraryDocument>(`${API}/documents/${id}`, {
        params: { include_download_url: includeDownloadUrl },
    });
    return result.data;
};

export const getPresignedUploadUrl = async (filename: string) => {
    const result = await axios.post<IPresignedUploadResponse>(
        `${API}/upload-url`,
        null,
        { params: { filename } }
    );
    return result.data;
};

export const uploadToS3 = async (
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
) => {
    await storageAxios.put(uploadUrl, file, {
        headers: {
            "Content-Type": file.type,
        },
        signal,
        onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
                const progress = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                onProgress(progress);
            }
        },
    });
};

export const checkLibraryDuplicate = async (objectKey: string) => {
    const result = await axios.post<IDuplicateCheckResponse>(
        `${API}/documents/check-duplicate`,
        { object_key: objectKey }
    );
    return result.data;
};

export const confirmLibraryUpload = async (data: ILibraryUploadConfirm) => {
    const result = await axios.post<ILibraryDocument>(`${API}/documents`, data);
    return result.data;
};

export const updateLibraryDocument = async (
    id: number,
    data: ILibraryDocumentUpdate
) => {
    const result = await axios.put<ILibraryDocument>(
        `${API}/documents/${id}`,
        data
    );
    return result.data;
};

export const moveLibraryDocument = async (
    id: number,
    data: ILibraryDocumentMoveRequest
) => {
    const result = await axios.put<ILibraryDocument>(
        `${API}/documents/${id}/move`,
        data
    );
    return result.data;
};

export const deleteLibraryDocument = async (id: number) => {
    await axios.delete(`${API}/documents/${id}`);
};

export const getDocumentRelated = async (id: number) => {
    const result = await axios.get<ILibraryDocumentRelated>(
        `${API}/documents/${id}/related`
    );
    return result.data;
};

export const getDocumentProcessingStatus = async (id: number) => {
    const result = await axios.get<IDocumentProcessingStatus>(
        `${API}/documents/${id}/status`
    );
    return result.data;
};

export const updateLibraryScope = async (
    id: number,
    data: ILibraryScopeUpdate
) => {
    const result = await axios.patch<ILibraryListScope>(
        `${API}/scopes/${id}`,
        data
    );
    return result.data;
};

export const updateLibraryComparison = async (
    id: number,
    data: ILibraryComparisonUpdate
) => {
    const result = await axios.patch<ILibraryListComparison>(
        `${API}/comparisons/${id}`,
        data
    );
    return result.data;
};

export const getLibraryList = async ({
    page = 1,
    size = 50,
    ...rest
}: ILibraryListParams) => {
    const url = buildUrl(`${API}/list`, {
        page,
        size,
        ...(rest.folder_id != null ? { folder_id: rest.folder_id } : {}),
        ...(rest.is_knowledge_base != null
            ? { is_knowledge_base: rest.is_knowledge_base }
            : {}),
        ...(rest.type_filter ? { type_filter: rest.type_filter } : {}),
        ...(rest.q ? { q: rest.q } : {}),
        ...(rest.sort_by ? { sort_by: rest.sort_by } : {}),
        ...(rest.order ? { order: rest.order } : {}),
        ...(rest.client_id != null ? { client_id: rest.client_id } : {}),
        ...(rest.includeShared ? { include_shared: true } : {}),
    });
    const result = await axios.get<ILibraryListPage>(url);
    return result.data;
};
